import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiMap, MiMarker, MiPopper } from "@miplatform/components";
import { USER_COLORS } from "@miplatform/constants";
import { IAssignment, IOrderInfo, OrderStatus } from "@miplatform/types";
import { getContrastColor } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import type { MapMouseEvent } from "@vis.gl/react-google-maps";
import { DirectionsRoute } from "../../../components/DirectionsRoute/DirectionsRoute";
import { OrderInfoCard } from "../OrdersList/OrderInfoCard/OrderInfoCard";
import { IAssignmentCalendarUser } from "./AssignmentsCalendar.types";
import {
  getAssignmentMarker,
  getLocationBoundariesFromAssignments,
} from "./AssignmentsMap.util";
import { AssignmentsUserCard } from "./AssignmentsUserCard";

interface AssignmentsMapProps {
  title: string;
  users: IAssignmentCalendarUser[];
}

export const AssignmentsMap = ({ users, title }: AssignmentsMapProps) => {
  const timeoutRef = useRef<number | null>(null);
  const { theme, classes } = useStyles();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentAssignment, setCurrentAssignment] =
    useState<IAssignment | null>(null);
  const filteredUsers = useMemo(() => {
    if (selectedUsers.length === 0) {
      return users;
    }

    return users.filter((user) => selectedUsers.includes(user.id));
  }, [users, selectedUsers]);
  const { position, zoomFactor } = useMemo(() => {
    const assignments = users.flatMap((user) => user.currentAssignments);

    return getLocationBoundariesFromAssignments(assignments);
  }, [users]);
  const directionsRoutes = useMemo(
    () =>
      filteredUsers.reduce((acc, user) => {
        acc[user.id] = user.currentAssignments
          .map(
            (assignment) =>
              ({
                lat: assignment.home?.address?.location?.latitude,
                lng: assignment.home?.address?.location?.longitude,
              } as { lat: number; lng: number })
          )
          .filter((loc) => loc.lat && loc.lng);

        return acc;
      }, {} as Record<string, { lat: number; lng: number }[]>),
    [filteredUsers]
  );
  const { userColors, userCounts } = useMemo(() => {
    let colorIndex = 0;
    return users.reduce(
      (acc, user) => {
        const color = USER_COLORS[colorIndex];
        colorIndex = (colorIndex + 1) % USER_COLORS.length;
        acc.userColors[user.id] = color;
        acc.userCounts[user.id] = user.currentAssignments.length;

        return acc;
      },
      { userColors: {}, userCounts: {} } as {
        userColors: Record<string, string>;
        userCounts: Record<string, number>;
      }
    );
  }, [users]);

  const handleUserClick = (userId: string) => {
    setSelectedUsers((prevSelectedUsers) =>
      prevSelectedUsers.includes(userId)
        ? prevSelectedUsers.filter((id) => id !== userId)
        : [...prevSelectedUsers, userId]
    );
  };

  const handlePopperOpen = (e: MapMouseEvent, assignment: IAssignment) => {
    const { currentTarget } = e.domEvent as {
      currentTarget: HTMLElement | null;
    };
    const timeout = setTimeout(() => {
      setAnchorEl(currentTarget);
      setCurrentAssignment(assignment);
    }, 500);
    timeoutRef.current = timeout;
  };

  const handlePopperClose = () => {
    setAnchorEl(null);
    setCurrentAssignment(null);
    clearTimeout(timeoutRef.current ?? undefined);
  };

  useEffect(() => {
    setSelectedUsers([]);
  }, [users]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>{title}</Typography>
      </Box>
      <Box className={classes.content}>
        <Box className={classes.usersContainer}>
          {users.map((user) => (
            <AssignmentsUserCard
              key={user.id}
              displayName={
                user.displayName ||
                `${user.firstName || ""} ${user.lastName || ""}`
              }
              color={userColors[user.id]}
              count={userCounts[user.id]}
              isSelected={selectedUsers.includes(user.id)}
              onClick={() => handleUserClick(user.id)}
            />
          ))}
        </Box>
        <Box className={classes.mapContainer}>
          <MiMap
            zoomFactor={zoomFactor}
            initialMapPosition={position}
            onClick={handlePopperClose}
            onDrag={handlePopperClose}
            onZoomChanged={handlePopperClose}
          >
            {filteredUsers.map((user) => (
              <Fragment key={user.id}>
                <DirectionsRoute
                  points={directionsRoutes[user.id]}
                  color={userColors[user.id]}
                />
                {user.currentAssignments.map((assignment, index) => (
                  <Box key={assignment.id}>
                    <MiMarker
                      position={{
                        lat: assignment.home?.address?.location
                          ?.latitude as number,
                        lng: assignment.home?.address?.location
                          ?.longitude as number,
                      }}
                      label={{
                        text: (index + 1).toString(),
                        color: getContrastColor(userColors[user.id]),
                      }}
                      icon={getAssignmentMarker(
                        userColors[user.id],
                        assignment.order.orderStatus as OrderStatus,
                        theme
                      )}
                      onMouseOver={(e: MapMouseEvent) => {
                        handlePopperOpen(e, assignment);
                      }}
                      onMouseOut={handlePopperClose}
                    />
                    {currentAssignment?.id === assignment.id && (
                      <MiPopper
                        anchorEl={anchorEl}
                        arrowColor={currentAssignment?.order.orderTemplateColor}
                        placement="auto-end"
                        showArrow
                      >
                        <OrderInfoCard
                          hideEmails
                          order={currentAssignment?.order as IOrderInfo}
                          home={currentAssignment?.home}
                        />
                      </MiPopper>
                    )}
                  </Box>
                ))}
              </Fragment>
            ))}
          </MiMap>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: "100%",
    flex: 1,
  },
  header: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.common.white,
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
    height: 28,
    backgroundColor: theme.palette.grey.darkerAccent,
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    overflowY: "hidden",
  },
  title: {
    fontSize: theme.fontSize.medium,
    fontWeight: theme.fontWeight.semiBold,
    flex: 1,
    minWidth: 122,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    padding: theme.spacing(1),
  },
  usersContainer: {
    display: "flex",
    gap: theme.spacing(1),
    overflowX: "auto",
  },
  mapContainer: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flex: 1,
  },
}));
